/* Loom Loom — sticky free-shipping bar (homepage section 12)
 *
 * Reads live cart state and recomputes on every cart mutation:
 *   - Dawn's pub/sub `cart-update` event (quantity changes, product-form adds)
 *   - the theme's `cart:updated` / `cart:refresh` DOM events
 *   - any re-render of #cart-icon-bubble (membership toggle, reversible builder
 *     and other scripts refresh that section after touching the cart)
 * Falls back to fetching /cart.js when the event carries no cart totals.
 *
 * Hidden when the cart is empty and while the cart drawer is open.
 */
(function () {
  'use strict';
  if (customElements.get('ll-shipping-bar')) return;

  function formatMoney(cents, format) {
    var value = (cents / 100);
    var fmt = format || '${{amount}}';
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    var key = m ? m[1] : 'amount';
    var str;
    function withDelims(num, decimals, thousands, decimal) {
      var s = num.toFixed(decimals);
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    switch (key) {
      case 'amount_no_decimals': str = withDelims(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': str = withDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': str = withDelims(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': str = withDelims(value, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': str = withDelims(value, 0, ' ', '.'); break;
      case 'amount_with_space_separator': str = withDelims(value, 2, ' ', ','); break;
      default: str = withDelims(value, 2, ',', '.');
    }
    // Trim ".00" so the bar reads "$19" like the rest of the page.
    str = str.replace(/([.,])00$/, '');
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, str).replace(/<[^>]*>/g, '');
  }

  class LLShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 8400;
      this.format = this.dataset.moneyFormat || '${{amount}}';
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.count = parseInt(this.dataset.count, 10) || 0;
      this.line = this.querySelector('[data-line]');
      this.fill = this.querySelector('[data-fill]');
      this.track = this.querySelector('[data-track]');
      this.btn = this.querySelector('[data-open-cart]');
      this.btnLabel = this.querySelector('[data-btn-label]');
      this.drawerOpen = false;
      this.mq = window.matchMedia('(max-width: 749px)');

      if (this.btn) this.btn.addEventListener('click', this.openCart.bind(this));

      var self = this;
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          var cart = event && event.cartData;
          if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
            self.apply(cart);
          } else {
            self.refresh();
          }
        });
      }
      ['cart:updated', 'cart:refresh'].forEach(function (name) {
        document.addEventListener(name, function () { self.refresh(); });
      });

      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && 'MutationObserver' in window) {
        this.bubbleObserver = new MutationObserver(function () { self.refresh(); });
        this.bubbleObserver.observe(bubble, { childList: true, subtree: true });
      }

      var drawer = document.querySelector('cart-drawer');
      if (drawer && 'MutationObserver' in window) {
        this.drawerObserver = new MutationObserver(function () {
          self.drawerOpen = drawer.classList.contains('active');
          self.updateVisibility();
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      this.mq.addEventListener ? this.mq.addEventListener('change', function () { self.render(); }) : this.mq.addListener(function () { self.render(); });
      window.addEventListener('resize', function () { self.reserveSpace(); });

      this.render();
      // The server-rendered numbers can be stale on back/forward navigation.
      this.refresh();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.bubbleObserver) this.bubbleObserver.disconnect();
      if (this.drawerObserver) this.drawerObserver.disconnect();
      document.body.style.removeProperty('padding-bottom');
    }

    refresh() {
      var self = this;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(function () {
        var url = ((window.routes && routes.cart_url) || '/cart') + '.js';
        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { self.apply(cart); })
          .catch(function () { /* keep the last known state */ });
      }, 150);
    }

    apply(cart) {
      if (!cart) return;
      this.total = typeof cart.total_price === 'number' ? cart.total_price : this.total;
      this.count = typeof cart.item_count === 'number' ? cart.item_count : this.count;
      this.render();
    }

    render() {
      var remaining = Math.max(0, this.threshold - this.total);
      var pct = this.threshold > 0 ? Math.min(100, Math.round(this.total / this.threshold * 100)) : 100;
      var totalText = formatMoney(this.total, this.format);

      if (this.line) {
        this.line.textContent = remaining > 0
          ? (this.dataset.remainingText || '{amount} to free shipping').replace('{amount}', formatMoney(remaining, this.format))
          : (this.dataset.unlockedText || 'Free shipping unlocked');
      }
      if (this.fill) this.fill.style.width = pct + '%';
      if (this.track) this.track.setAttribute('aria-valuenow', String(pct));
      if (this.btnLabel) {
        var template = this.mq.matches
          ? (this.dataset.buttonTextMobile || 'Cart — {total}')
          : (this.dataset.buttonText || 'View cart — {total}');
        this.btnLabel.textContent = template.replace('{total}', totalText);
      }
      this.classList.toggle('is-unlocked', remaining === 0);
      this.updateVisibility();
    }

    updateVisibility() {
      var hidden = this.count === 0 || this.drawerOpen;
      this.classList.toggle('is-hidden', hidden);
      this.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      this.reserveSpace();
    }

    reserveSpace() {
      // Keep the bar from covering the footer.
      if (this.classList.contains('is-hidden')) {
        document.body.style.removeProperty('padding-bottom');
      } else {
        document.body.style.paddingBottom = this.offsetHeight + 'px';
      }
    }

    openCart() {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this.btn);
      } else {
        window.location.href = (window.routes && routes.cart_url) || '/cart';
      }
    }
  }

  customElements.define('ll-shipping-bar', LLShippingBar);
})();
